import { LoaderFunctionArgs } from 'react-router-dom';
import { client } from '../../../../../gql/client';
import { GET_ACTION_BUDGET_ITEMS } from '../../../../../gql/queries/budget/actionBudgetItems';
import { BudgetItemsLoaderData, ActionBudgetItemRow } from './types';
import { selectBudgetItemsValue } from '../../../../../gql/selectors/actions';
import debug from 'debug';

// Constants
import {
  formActionNaming,
  formActionValidationNaming,
} from '../../../../../components/forms/ActionForms/constants';

// Utils
import { decompressSessionStorage } from '../../../../../utils/sessionStorage';
import { reconcileValidationActionContext } from '../../../../../utils/validationActionContext';
import { claimDraftScope } from '../../../../../utils/draftOwnership';
import { ACTION_MEDIA_SCOPE } from '../../../../../utils/draftScopes';

const loadFormValues = async (actionId: string) => {
  const logBudgetLoader = debug(
    'rois:budgetitems:loader:validation:formvalues'
  );

  try {
    logBudgetLoader('🔍 [loadFormValues] Iniciando para actionId:', actionId);
    const formValues = await selectBudgetItemsValue(actionId);
    logBudgetLoader('✅ [loadFormValues] Form values obtenidos:', formValues);
    return formValues;
  } catch (err) {
    logBudgetLoader('❌ [loadFormValues] Error:', err);
    console.log('> Error loading budget items data', err);
    return {
      isBudgetItemsTableDisplayed: 'false',
      budgetItemsTable: {},
    };
  }
};

const loadBudgetItemsData = async ({ params }: LoaderFunctionArgs) => {
  const logBudgetLoader = debug('rois:budgetitems:loader:validation');
  const actionId = params.id;

  logBudgetLoader(
    '🔍 [ActionsValidation] Iniciando carga de Budget Items para actionId:',
    actionId
  );

  if (!actionId) {
    throw new Error('Action ID is required');
  }

  // Leer roid y orderId del contexto de acción en sessionStorage
  let roid: string | undefined;
  let orderId: string | undefined;
  try {
    const actionRaw = sessionStorage.getItem(formActionNaming.NEW_VALIDATION_ACTION_DATA);
    if (actionRaw) {
      const actionData = JSON.parse(decompressSessionStorage(actionRaw));
      roid = actionData.roid;
      orderId = actionData.orderId;
    }
  } catch {
    // ignore
  }

  // Cargar datos de sessionStorage si existen
  let sessionStorageData: any = {};
  try {
    const budgetItemsRaw = sessionStorage.getItem(
      formActionValidationNaming.NEW_VALIDATION_BUDGET_ITEMS_FORM
    );
    logBudgetLoader(
      '📦 [ActionsValidation] SessionStorage raw data:',
      budgetItemsRaw
    );
    if (budgetItemsRaw) {
      sessionStorageData = JSON.parse(decompressSessionStorage(budgetItemsRaw));
      logBudgetLoader(
        '📦 [ActionsValidation] SessionStorage parsed data:',
        sessionStorageData
      );
    }
  } catch (error) {
    logBudgetLoader(
      '❌ [ActionsValidation] Error reading budget items from sessionStorage:',
      error
    );
    console.error('Error reading budget items from sessionStorage:', error);
  }

  try {
    // ✅ Usar el selector para obtener datos en formato correcto
    logBudgetLoader(
      '🌐 [ActionsValidation] Ejecutando selectBudgetItemsValue con actionId:',
      actionId
    );
    const formValues = await loadFormValues(actionId);
    logBudgetLoader(
      '🔄 [ActionsValidation] Form values del selector:',
      formValues
    );

    // ✅ ESTRUCTURA CORRECTA: Crear estructura compatible con formTableUtils
    // PROBLEMA: formValues.budgetItemsTable tiene claves como actionBudgetItemId,
    // pero dataFromServer debe tener claves como actionBudgetItemId para que formTableUtils funcione
    const serverDataWithCorrectKeys: Record<string, any> = {};

    // Convertir las claves de budgetItemId a actionBudgetItemId
    Object.values(formValues.budgetItemsTable || {}).forEach((item: any) => {
      if (item.id) {
        serverDataWithCorrectKeys[item.id] = item;
      }
    });

    logBudgetLoader(
      '🔧 [ActionsValidation] Server data with correct keys:',
      serverDataWithCorrectKeys
    );

    // ✅ PATRÓN MATERIALS: sessionStorage sobrescribe, no mezcla
    logBudgetLoader('🔍 [MERGE LOGIC] Datos antes de combinar:');
    logBudgetLoader(
      '  - formValues.budgetItemsTable keys:',
      Object.keys(formValues.budgetItemsTable || {})
    );
    logBudgetLoader(
      '  - sessionStorageData.budgetItemsTable keys:',
      Object.keys(sessionStorageData.budgetItemsTable || {})
    );
    logBudgetLoader(
      '  - sessionStorageData.budgetItemsTable existe?:',
      !!sessionStorageData.budgetItemsTable
    );

    const finalBudgetItemsTable =
      sessionStorageData.budgetItemsTable || formValues.budgetItemsTable;

    logBudgetLoader('🎯 [MERGE LOGIC] Tabla final seleccionada:');
    logBudgetLoader(
      '  - Fuente:',
      sessionStorageData.budgetItemsTable ? 'sessionStorage' : 'servidor'
    );
    logBudgetLoader(
      '  - Keys en tabla final:',
      Object.keys(finalBudgetItemsTable || {})
    );

    const combinedData = {
      actionId,
      roid,
      orderId,
      // Si hay datos en sessionStorage, usar esos; si no, usar los del servidor
      isBudgetItemsTableDisplayed:
        sessionStorageData.isBudgetItemsTableDisplayed !== undefined
          ? sessionStorageData.isBudgetItemsTableDisplayed
          : formValues.isBudgetItemsTableDisplayed,
      // ✅ CRÍTICO: sessionStorage SOBRESCRIBE, no mezcla (evita duplicados)
      budgetItemsTable: finalBudgetItemsTable,
      // ✅ CRÍTICO: Mantener datos originales del servidor para comparaciones
      dataFromServer: {
        budgetItemsTable: serverDataWithCorrectKeys,
      },
    };

    logBudgetLoader(
      '✅ [ActionsValidation] Combined data final:',
      combinedData
    );
    logBudgetLoader(
      '🔍 [ActionsValidation] dataFromServer structure:',
      combinedData.dataFromServer
    );

    return combinedData;
  } catch (error) {
    logBudgetLoader(
      '❌ [ActionsValidation] Error loading budget items:',
      error
    );
    console.error('Error loading budget items:', error);
    return {
      actionId,
      isBudgetItemsTableDisplayed: 'false',
      budgetItemsTable: {},
    };
  }
};

export const budgetItemsLoader = async (args: LoaderFunctionArgs) => {
  // La URL manda: si el contexto de la pestaña es de OTRA actuación se borra
  // entero y esta carga sale por el `:id` de la URL. Síncrono y antes de
  // cualquier `await` (diagnóstico 2026-10-08 § 8).
  reconcileValidationActionContext(args.params.id);
  // La lista de fotos y el croquis en curso también son de la actuación
  // de la URL. Las carpetas de OPFS no se tocan: las comparten las
  // pestañas (spec 2026-11-02 wizard-draft-ownership § 6).
  claimDraftScope(ACTION_MEDIA_SCOPE, args.params.id);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  const logBudgetLoader = debug('rois:budgetitems:loader:validation:main');

  logBudgetLoader(
    '🚀 [budgetItemsLoader] Iniciando loader principal para actionId:',
    args.params.id
  );

  // As this is a Loader that communicates with an API, add the `defer` function to return data asynchronously
  // which combines with the Fallback component wrapping the page to show loaders properly
  const loaderToResolve = loadBudgetItemsData(args);

  logBudgetLoader('🚀 [budgetItemsLoader] Loader configurado con defer');

  return loaderToResolve;
};

  return { data: load() };
};
